import {
  MessageId,
  type ChannelThreadId,
  type SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageCursor,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type {
  SupportTicketThreadContext,
  SupportTicketThreadResponse,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  getSupportTicketIdScope,
  type SupportTicketCollectionFilter,
} from "./support-ticket-collection-scope";

/** Shape hooks return: entity fields + per-ticket link fields from the join. */
export type SupportTicketThreadCollectionItem = ChannelThread & {
  ticketContext?: SupportTicketThreadContext;
  messageCount?: number;
  outsideMessageCount?: number;
};

export interface SupportTicketThreadLink {
  threadId: ChannelThreadId;
  ticketContext?: SupportTicketThreadContext;
  messageCount?: number;
  outsideMessageCount?: number;
}

export interface SupportTicketMessageLink {
  messageId: MessageId;
}

export interface SupportTicketTranscriptApi {
  getThreads(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly SupportTicketThreadResponse[] }>;
  /** A page of the ticket's messages, newest first; `cursor` reaches further back. */
  getMessages(
    supportTicketId: SupportTicketId,
    cursor?: MessageCursor,
  ): Promise<{ items: readonly Message[]; nextCursor: MessageCursor | null }>;
  getTimelineEvents(
    supportTicketId: SupportTicketId,
  ): Promise<{ items: readonly SupportTicketTimelineEventResponse[] }>;
  getThreadMessages(
    supportTicketId: SupportTicketId,
    threadId: ChannelThreadId,
  ): Promise<{ items: readonly Message[] }>;
}

export function getSupportTicketIdFilter(
  filters: readonly SupportTicketCollectionFilter[],
): SupportTicketId | null {
  return getSupportTicketIdScope(filters);
}

export interface SupportTicketThreadsFetchResult {
  entities: ChannelThread[];
  links: SupportTicketThreadLink[];
}

export interface SupportTicketMessagesFetchResult {
  entities: Message[];
  links: SupportTicketMessageLink[];
  /** Where the next older page starts; null once the oldest message is loaded. */
  earlierCursor: MessageCursor | null;
}

export async function fetchSupportTicketThreads(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketThreadsFetchResult> {
  const result = await api.getThreads(supportTicketId);
  const entities: ChannelThread[] = [];
  const links: SupportTicketThreadLink[] = [];
  for (const {
    thread,
    ticketContext,
    messageCount,
    outsideMessageCount,
  } of result.items) {
    entities.push(thread);
    links.push({
      threadId: thread.id,
      ...(ticketContext ? { ticketContext } : {}),
      ...(messageCount !== undefined ? { messageCount } : {}),
      ...(outsideMessageCount !== undefined ? { outsideMessageCount } : {}),
    });
  }
  return { entities, links };
}

/** Whether `a` sorts after `b` in transcript (sentAt, id) order. */
export function isMessageCursorAfter(
  a: MessageCursor,
  b: MessageCursor,
): boolean {
  const sentAtDelta = a.sentAt.getTime() - b.sentAt.getTime();
  return sentAtDelta !== 0
    ? sentAtDelta > 0
    : MessageId.toBigInt(a.id) > MessageId.toBigInt(b.id);
}

export function isSameMessageCursor(
  a: MessageCursor | null,
  b: MessageCursor | null,
): boolean {
  return a === null || b === null
    ? a === b
    : a.sentAt.getTime() === b.sentAt.getTime() && a.id === b.id;
}

function toMessagesFetchResult(
  entities: Message[],
  earlierCursor: MessageCursor | null,
): SupportTicketMessagesFetchResult {
  const links: SupportTicketMessageLink[] = entities.map((message) => ({
    messageId: message.id,
  }));
  return { entities, links, earlierCursor };
}

/** The newest page, then older pages back through `loadedThrough` when set. */
export async function fetchSupportTicketMessages(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
  loadedThrough: MessageCursor | null = null,
): Promise<SupportTicketMessagesFetchResult> {
  let page = await api.getMessages(supportTicketId);
  const pages: (readonly Message[])[] = [page.items];
  while (
    loadedThrough &&
    page.nextCursor &&
    isMessageCursorAfter(page.nextCursor, loadedThrough)
  ) {
    page = await api.getMessages(supportTicketId, page.nextCursor);
    pages.unshift(page.items);
  }
  return toMessagesFetchResult(pages.flat(), page.nextCursor);
}

/** One page older than `earlierCursor`, and the oldest message it reaches. */
export async function fetchEarlierSupportTicketMessages(
  api: SupportTicketTranscriptApi,
  supportTicketId: SupportTicketId,
  earlierCursor: MessageCursor,
): Promise<
  SupportTicketMessagesFetchResult & { loadedThrough: MessageCursor | null }
> {
  const page = await api.getMessages(supportTicketId, earlierCursor);
  const oldest = page.items[0];
  return {
    ...toMessagesFetchResult([...page.items], page.nextCursor),
    loadedThrough: oldest ? { sentAt: oldest.sentAt, id: oldest.id } : null,
  };
}
